import { useEffect, useMemo, useState } from 'react';
import {
  api,
  type AdminShopSale,
  type ShopSaleCatalogItem,
  type ShopSaleItemType,
} from '@/api/client';
import { ErrorBox, Loading, PageTitle } from '@/components/ui';

const TYPE_LABEL: Record<ShopSaleItemType, string> = {
  cosmetic: 'Cosmetics',
  character: 'Characters',
  holdSkin: 'Hold skins',
  bundle: 'Combos',
  clanEmblem: 'Clan logos',
  skill: 'Skills',
};

function key(type: ShopSaleItemType, id: string): string {
  return `${type}:${id}`;
}

export function ShopSalesPage() {
  const [catalog, setCatalog] = useState<ShopSaleCatalogItem[] | null>(null);
  const [sales, setSales] = useState<AdminShopSale[]>([]);
  const [filter, setFilter] = useState<'all' | ShopSaleItemType>('all');
  const [search, setSearch] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      const result = await api.shopSales();
      setCatalog(result.catalog);
      setSales(result.sales);
      setErr(null);
    } catch (error) {
      setErr(String(error));
    }
  };

  useEffect(() => { void load(); }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (catalog ?? []).filter((item) => (
      (filter === 'all' || item.itemType === filter)
      && (!q || item.name.toLowerCase().includes(q) || item.subtype.toLowerCase().includes(q))
    ));
  }, [catalog, filter, search]);
  const saleByItem = useMemo(
    () => new Map(sales.map((sale) => [key(sale.itemType, sale.itemId), sale])),
    [sales],
  );

  if (err && !catalog) return <ErrorBox text={err} />;
  if (!catalog) return <Loading />;

  return (
    <div>
      <PageTitle title="Shop Sales" />
      {err && <ErrorBox text={err} />}
      <div
        className="card"
        style={{
          padding: '11px 14px',
          marginBottom: 16,
          color: 'var(--ink-muted)',
          fontSize: 12,
          background: 'rgba(230,57,70,.07)',
          borderColor: 'rgba(230,57,70,.3)',
        }}
      >
        Schedule one discount per item. Active prices are calculated again by the server when the
        player buys, so changing or ending a sale takes effect immediately. Real-money IAP prices
        remain managed in App Store Connect and Google Play Console.
      </div>

      <div className="card" style={{ padding: 12, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search item…"
          style={{ flex: '1 1 220px' }}
        />
        <select value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)}>
          <option value="all">All item types</option>
          {Object.entries(TYPE_LABEL).map(([type, label]) => (
            <option key={type} value={type}>{label}</option>
          ))}
        </select>
        <div className="mono" style={{ alignSelf: 'center', color: 'var(--ink-dim)', fontSize: 11 }}>
          {rows.length} items · {sales.filter((sale) => sale.isActive).length} enabled sales
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((item) => (
          <SaleRow
            key={key(item.itemType, item.id)}
            item={item}
            sale={saleByItem.get(key(item.itemType, item.id)) ?? null}
            onChanged={load}
          />
        ))}
        {rows.length === 0 && (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--ink-dim)' }}>No matching items.</div>
        )}
      </div>
    </div>
  );
}

function inputDate(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function isoDate(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

function status(sale: AdminShopSale | null): { text: string; color: string } {
  if (!sale) return { text: 'NO SALE', color: 'var(--ink-dim)' };
  if (!sale.isActive) return { text: 'DISABLED', color: 'var(--ink-dim)' };
  const now = Date.now();
  if (sale.startsAt && new Date(sale.startsAt).getTime() > now) return { text: 'SCHEDULED', color: 'var(--c-blue)' };
  if (sale.endsAt && new Date(sale.endsAt).getTime() <= now) return { text: 'ENDED', color: 'var(--c-red)' };
  return { text: 'LIVE', color: 'var(--c-cyan)' };
}

function discounted(value: number, percent: number): number {
  return value <= 0 ? 0 : Math.max(1, Math.floor(value * (100 - percent) / 100));
}

function SaleRow({
  item,
  sale,
  onChanged,
}: {
  item: ShopSaleCatalogItem;
  sale: AdminShopSale | null;
  onChanged: () => Promise<void>;
}) {
  const [percent, setPercent] = useState(sale?.discountPercent ?? 20);
  const [label, setLabel] = useState(sale?.label ?? 'SALE');
  const [startsAt, setStartsAt] = useState(inputDate(sale?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(inputDate(sale?.endsAt ?? null));
  const [enabled, setEnabled] = useState(sale?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const state = status(sale);

  useEffect(() => {
    setPercent(sale?.discountPercent ?? 20);
    setLabel(sale?.label ?? 'SALE');
    setStartsAt(inputDate(sale?.startsAt ?? null));
    setEndsAt(inputDate(sale?.endsAt ?? null));
    setEnabled(sale?.isActive ?? true);
  }, [sale]);

  const save = async () => {
    setBusy(true);
    try {
      await api.saveShopSale(item.itemType, item.id, {
        discountPercent: percent,
        label: label.trim() || null,
        startsAt: isoDate(startsAt),
        endsAt: isoDate(endsAt),
        isActive: enabled,
      });
      await onChanged();
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!sale || !confirm(`Remove sale for "${item.name}"?`)) return;
    setBusy(true);
    try {
      await api.deleteShopSale(item.itemType, item.id);
      await onChanged();
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 13, display: 'grid', gridTemplateColumns: 'minmax(190px, 1.3fr) minmax(300px, 2fr) auto', gap: 14, alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <div style={{ width: 48, height: 48, flexShrink: 0, borderRadius: 9, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,.3)', overflow: 'hidden' }}>
          {item.imageUrl
            ? <img src={item.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            : <span style={{ color: 'var(--ink-dim)', fontSize: 18 }}>◇</span>}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
          <div className="tag" style={{ marginTop: 5 }}>{TYPE_LABEL[item.itemType]} · {item.subtype}</div>
          <div className="mono" style={{ marginTop: 5, color: 'var(--ink-muted)', fontSize: 10 }}>
            {item.priceCoins > 0 && `${item.priceCoins.toLocaleString()} coins`}
            {item.priceCoins > 0 && item.priceGems > 0 && ' · '}
            {item.priceGems > 0 && `${item.priceGems.toLocaleString()} gems`}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '88px 110px minmax(150px, 1fr) minmax(150px, 1fr)', gap: 7 }}>
        <label className="tag">DISCOUNT %<input type="number" min={1} max={95} value={percent} onChange={(event) => setPercent(Math.max(1, Math.min(95, Number(event.target.value))))} style={{ width: '100%', marginTop: 5 }} /></label>
        <label className="tag">RIBBON<input maxLength={32} value={label} onChange={(event) => setLabel(event.target.value)} style={{ width: '100%', marginTop: 5 }} /></label>
        <label className="tag">START (OPTIONAL)<input type="datetime-local" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} style={{ width: '100%', marginTop: 5 }} /></label>
        <label className="tag">END (OPTIONAL)<input type="datetime-local" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} style={{ width: '100%', marginTop: 5 }} /></label>
        <div className="mono" style={{ gridColumn: '1 / -1', color: 'var(--c-yellow)', fontSize: 10 }}>
          Preview: {discounted(item.priceCoins, percent).toLocaleString()} coins · {discounted(item.priceGems, percent).toLocaleString()} gems
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 100 }}>
        <span className="tag" style={{ color: state.color, textAlign: 'center' }}>{state.text}</span>
        <label style={{ fontSize: 11, textAlign: 'center' }}>
          <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} /> enabled
        </label>
        <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>{busy ? 'SAVING…' : sale ? 'UPDATE' : 'CREATE'}</button>
        {sale && <button className="btn" disabled={busy} onClick={() => void remove()} style={{ color: 'var(--c-red)' }}>REMOVE</button>}
      </div>
    </div>
  );
}
